/**
 * Quote Service
 * Handles creating and sending quotes to clients
 */

import { toast } from 'sonner';
import { apiClient as api } from '@/lib/api';
import { buildItineraryProposalEmailHtml } from '@/utils/emailTemplates';

/**
 * Resolve the checkout amount for the /checkout/token call.
 *
 * The BE rejects `amount: 0` with a misleading "amount is required" error
 * (the payment gateway can't tokenize a $0 charge). Callers used to send
 * `settings.final_price || total_due || 0` directly, so a broker who never
 * opened the Customize modal — leaving `settings.final_price` at its empty
 * initial — would slip a 0 through and hit that BE error.
 *
 * Resolution order:
 *   1. quoteData.settings.final_price   (client-facing final)
 *   2. quoteData.total_due              (older field kept for compat)
 *   3. sum of bidOptions.final_price    (per-option totals from the
 *                                        pricing card; catches the case
 *                                        where the Customize modal was
 *                                        skipped but the per-option
 *                                        pricing IS filled in)
 *
 * Throws when none of them resolve to > 0 so we never hit the BE with a
 * $0 charge. The caller's `.catch` surfaces the message as a toast.
 */
const resolveCheckoutAmount = (quoteData) => {
  const candidates = [
    parseFloat(quoteData?.settings?.final_price),
    parseFloat(quoteData?.total_due),
    Array.isArray(quoteData?.bidOptions)
      ? quoteData.bidOptions.reduce((sum, opt) => sum + (parseFloat(opt?.final_price) || 0), 0)
      : 0,
  ];
  const amount = candidates.find((v) => Number.isFinite(v) && v > 0);
  if (!amount) {
    throw new Error('Quote total is $0 — set a client-facing price before sending.');
  }
  return amount;
};
/**
 * Creates a new quote
 * @param {Object} quoteData - Complete quote data
 * @returns {Promise<Object>} Promise resolving to created quote object
 */
export const createQuote = async (quoteData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 800));

  // Validate quote data
  if (!quoteData.client) {
    throw new Error('Client information is required');
  }

  if (!quoteData.aircraft) {
    throw new Error('Aircraft selection is required');
  }

  if (!quoteData.flights || quoteData.flights.length === 0) {
    throw new Error('Flight details are required');
  }

  if (!quoteData.settings?.bidTitle) {
    throw new Error('Quote title is required');
  }

  // Create quote object
  const quote = {
    id: Date.now(),
    ...quoteData,
    status: 'draft',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };

  return quote;
};

/**
 * Send the itinerary-proposal email for a quote that has ALREADY been
 * created/updated via the nested-write endpoint (`POST /quotes-with-bids` or
 * `PATCH /quotes-with-bids/:id`). The write step produces a fully-hydrated
 * quote (bid_items → bid → bid_options → aircraft(+custom_profile) →
 * operator, plus client / creator / sender), so this helper does NOT touch
 * the DB — just mints a checkout token and dispatches the email.
 *
 * Prior versions of this function also POST'd /quotes and re-fetched via
 * getQuoteById to expand the include tree the email template needs. The
 * nested-write response is already that tree, so both calls are gone.
 *
 * @param {Object} quote        - Fully-hydrated quote (write-response shape)
 * @param {Object} args
 * @param {Object} args.quoteData     - Form-state quote (recipient, totals, subject, …)
 * @param {Object} [args.organization] - Broker org record for email branding
 * @param {Object} [args.userProfile]  - Signed-in user (Cc / ReplyTo)
 * @returns {Promise<Object|undefined>} The quote passed in (for chaining), or
 *   undefined if the send was blocked by a validation guard.
 */
export const sendQuoteToClient = async (quote, { quoteData, organization = null, userProfile = null }) => {
  const recipientEmail = quoteData.client?.clientData?.email || quoteData.client?.email;
  if (recipientEmail === "quotes@trustjet.ai") {
    toast.warning('Email "From" cannot be the same as "To"', { position: 'bottom-right' });
    return;
  }

  const quoteId = quote?.quote_id;
  if (!quoteId) throw new Error('sendQuoteToClient requires a persisted quote with quote_id');

  const requestToken = await api.createCheckoutToken({
    quote_id: quoteId,
    amount: resolveCheckoutAmount(quoteData),
    currency: 'USD',
    payment_method: 'CREDIT_CARD',
    description: 'Charter flight quote payment',
    metadata: {
      customer_name: [
        quoteData.client?.clientData?.first_name || quoteData.client?.clientData?.firstName,
        quoteData.client?.clientData?.last_name || quoteData.client?.clientData?.lastName,
      ].filter(Boolean).join(' '),
      customer_email: recipientEmail,
    },
  });

  const brokerOrgId = quote?.broker_org_id || quoteData.broker_org_id || organization?.organization_id || '';
  const quoteLink = `${window.location.origin}/quotepreview?quote=${quoteId}&token=${requestToken?.token}&org=${brokerOrgId}`;

  const organizationEmail = userProfile?.email;

  const finalEmailMessage = await buildItineraryProposalEmailHtml({
    quote,
    quoteLink,
    passengers: quoteData.passengers || quote?.passengers || [],
    crew: quoteData.crew || null,
    services: quoteData.services || [],
    organization,
  });

  await api.sendMessage({
    From: 'quotes@trustjet.ai',
    To: recipientEmail,
    Cc: organizationEmail,
    ReplyTo: organizationEmail,
    Subject: quoteData.settings?.emailSubject || 'Itinerary Proposal for Review',
    HtmlBody: finalEmailMessage,
    Attachments: [],
  });

  return quote;
};

/**
 * Send an updated itinerary-proposal email for a quote that has ALREADY
 * been re-saved via `PATCH /quotes-with-bids/:id` (edit mode). The PATCH
 * response is a fully-hydrated quote — passed in directly here, so no
 * getQuoteById re-fetch is needed. Mints a fresh checkout token (so the
 * link reflects the latest amount) then dispatches the email.
 *
 * @param {Object} quote - Fully-hydrated quote (PATCH-response shape)
 * @param {Object} args
 * @param {Object} args.quoteData     - Form-state quote (recipient, totals, …)
 * @param {Object} [args.organization] - Broker org record for email branding
 * @param {Object} [args.userProfile]  - Signed-in user (Cc / ReplyTo)
 */
export const notifyClientOfQuoteUpdate = async (quote, { quoteData, organization = null, userProfile = null }) => {
  const quoteId = quote?.quote_id;
  if (!quoteId) throw new Error('notifyClientOfQuoteUpdate requires a persisted quote with quote_id');

  const recipientEmail = quoteData.client?.clientData?.email || quoteData.client?.email;
  if (!recipientEmail) {
    toast.warning('Client email is missing — cannot send update', { position: 'bottom-right' });
    return;
  }
  if (recipientEmail === 'quotes@trustjet.ai') {
    toast.warning('Email "From" cannot be the same as "To"', { position: 'bottom-right' });
    return;
  }

  const requestToken = await api.createCheckoutToken({
    quote_id: quoteId,
    amount: resolveCheckoutAmount(quoteData),
    currency: 'USD',
    payment_method: 'CREDIT_CARD',
    description: 'Charter flight quote payment',
    metadata: {
      customer_name: [
        quoteData.client?.clientData?.first_name || quoteData.client?.clientData?.firstName,
        quoteData.client?.clientData?.last_name || quoteData.client?.clientData?.lastName,
      ].filter(Boolean).join(' '),
      customer_email: recipientEmail,
    },
  });

  const brokerOrgId = quote?.broker_org_id || quoteData.broker_org_id || organization?.organization_id || '';
  const quoteLink = `${window.location.origin}/quotepreview?quote=${quoteId}&token=${requestToken?.token}&org=${brokerOrgId}`;

  const finalEmailMessage = await buildItineraryProposalEmailHtml({
    quote,
    quoteLink,
    passengers: quoteData.passengers || quote?.passengers || [],
    crew: quoteData.crew || null,
    services: quoteData.services || [],
    organization,
  });

  await api.sendMessage({
    From: 'quotes@trustjet.ai',
    To: recipientEmail,
    Cc: userProfile?.email,
    ReplyTo: userProfile?.email,
    Subject: quoteData.settings?.emailSubject || 'Updated Itinerary Proposal',
    HtmlBody: finalEmailMessage,
    Attachments: [],
  });

  return quote;
};

/**
 * Saves a quote as draft
 * @param {Object} quoteData - Quote data to save
 * @returns {Promise<Object>} Promise resolving to saved draft
 */
export const saveQuoteDraft = async (quoteData) => {
  // Simulate API delay
  await new Promise(resolve => setTimeout(resolve, 500));

  const draft = {
    id: Date.now(),
    ...quoteData,
    status: 'draft',
    savedAt: new Date().toISOString(),
  };

  return draft;
};

/**
 * Merge-safe patch for the `quote.files` JSON blob. The backend REPLACES the
 * whole `files` column on write, so a naive `updateQuote(id, { files: {...} })`
 * silently drops every sibling key (tripsheet URL, contract_signed, crews,
 * additional_services, fbo, approval audit, …). We re-read the latest files
 * immediately before writing so concurrent/earlier saves in the same flow
 * aren't clobbered, then spread the patch on top.
 *
 * Any non-files columns (status change, etc.) go in `extra` and are written
 * in the same PATCH call.
 *
 * @param {string} quoteId
 * @param {Object} filesPatch  Keys to merge into quote.files
 * @param {Object} [extra]     Non-files columns to include in the same update
 */
export const patchQuoteFiles = async (quoteId, filesPatch, extra = {}) => {
  let latestFiles = {};
  try {
    const fresh = await api.getQuoteById(quoteId);
    if (fresh?.files && typeof fresh.files === 'object') latestFiles = fresh.files;
  } catch (err) {
    console.warn("patchQuoteFiles: couldn't re-read latest files, writing patch only", err);
  }
  return api.updateQuote(quoteId, {
    ...extra,
    files: { ...latestFiles, ...filesPatch },
  });
};
